import { ComponentKey } from "enums/componentKey";
import { SearchFilterEnum } from "api/actor";
import { InputsConfig } from "types/form/inputsConfig";
import { addressInputsConfig } from "features/new-actor/form/inputs-config/addressInputsConfig";
import { PlateformeFormFieldNames } from "./PlateformeFormFieldNames";
import { PlateformeFormValues } from "./PlateformeFormValues";
import { themeInputsConfig } from "./themeInputsConfig";

/** Champs rendus via InputFactory — les rôles, comptes bancaires et type de
 * compagnie ont leurs propres composants et ne passent pas par ce config. */
export type PlateformeInputFieldName =
  | Exclude<keyof PlateformeFormValues, 'additionalRoles' | 'bankAccounts' | 'code' | 'sousNature'>
  // Absent de la création (défaut STANDARD côté serveur) : édition corporate uniquement.
  | PlateformeFormFieldNames.ConnectionMode;

export const plateformeInputsConfig: InputsConfig<PlateformeInputFieldName> = {
  ...addressInputsConfig,
  [PlateformeFormFieldNames.Name]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.Name,
    type: 'text',
    required: true,
    label: 'ACTOR_FORM.ORGANIZATION.LABELS.NAME',
    placeholder: 'ACTOR_FORM.ORGANIZATION.PLACEHOLDERS.NAME',
  },
  [PlateformeFormFieldNames.ConnectionMode]: {
    componentKey: ComponentKey.Dropdown,
    name: PlateformeFormFieldNames.ConnectionMode,
    required: true,
    label: 'ACTOR_FORM.ORGANIZATION.LABELS.CONNECTION_MODE',
    items: [
      { key: 'STANDARD', label: 'ACTOR_FORM.ORGANIZATION.CONNECTION_MODES.STANDARD' },
      { key: 'SSO_PARTENAIRE', label: 'ACTOR_FORM.ORGANIZATION.CONNECTION_MODES.SSO_PARTENAIRE' },
    ],
  },
  [PlateformeFormFieldNames.LogoFile]: {
    componentKey: ComponentKey.ImageUpload,
    name: PlateformeFormFieldNames.LogoFile,
    label: 'ACTOR_FORM.ORGANIZATION.LABELS.LOGO',
    placeholder: 'ACTOR_FORM.ORGANIZATION.PLACEHOLDERS.LOGO',
    dragPlaceholder: 'ACTOR_FORM.ORGANIZATION.PLACEHOLDERS.DRAG_LOGO',
  },
  [PlateformeFormFieldNames.MinimalLogoFile]: {
    componentKey: ComponentKey.ImageUpload,
    name: PlateformeFormFieldNames.MinimalLogoFile,
    label: 'ACTOR_FORM.ORGANIZATION.LABELS.MINIMAL_LOGO',
    placeholder: 'ACTOR_FORM.ORGANIZATION.PLACEHOLDERS.MINIMAL_LOGO',
    dragPlaceholder: 'ACTOR_FORM.ORGANIZATION.PLACEHOLDERS.DRAG_MINIMAL_LOGO',
  },
  // ---- Personne morale corporate principale ----
  [PlateformeFormFieldNames.Phone]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.Phone,
    type: 'tel',
    required: true,
    label: 'ACTOR_FORM.BROKER.LABELS.PHONE',
    placeholder: 'ACTOR_FORM.BROKER.PLACEHOLDERS.PHONE',
  },
  // SIREN (9 chiffres, l'entité légale) et non SIRET : la corporate représente
  // la société tête de plateforme, pas un établissement — cas unique.
  [PlateformeFormFieldNames.RegistrationNumber]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.RegistrationNumber,
    type: 'text',
    required: true,
    label: 'ACTOR_FORM.ORGANIZATION.LABELS.SIREN_NUMBER',
    placeholder: 'ACTOR_FORM.ORGANIZATION.PLACEHOLDERS.SIREN_NUMBER',
  },
  [PlateformeFormFieldNames.Capital]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.Capital,
    type: 'number',
    required: true,
    label: 'ACTOR_FORM.BROKER.LABELS.CAPITAL',
    placeholder: 'ACTOR_FORM.BROKER.PLACEHOLDERS.CAPITAL',
    min: 0,
  },
  [PlateformeFormFieldNames.RcsCity]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.RcsCity,
    type: 'text',
    required: true,
    label: 'ACTOR_FORM.BROKER.LABELS.RCS_CITY',
    placeholder: 'ACTOR_FORM.BROKER.PLACEHOLDERS.RCS_CITY',
  },
  [PlateformeFormFieldNames.RcsNumber]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.RcsNumber,
    type: 'text',
    required: true,
    label: 'ACTOR_FORM.BROKER.LABELS.RCS_NUMBER',
    placeholder: 'ACTOR_FORM.BROKER.PLACEHOLDERS.RCS_NUMBER',
  },
  [PlateformeFormFieldNames.OriasNumber]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.OriasNumber,
    type: 'text',
    required: true, // ORIAS obligatoire pour la nature Corporate (règle back OriasRequis)
    label: 'ACTOR_FORM.BROKER.LABELS.ORIAS_NUMBER',
    placeholder: 'ACTOR_FORM.BROKER.PLACEHOLDERS.ORIAS_NUMBER_REQUIRED',
  },
  [PlateformeFormFieldNames.Rcp]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.Rcp,
    type: 'text',
    required: false,
    label: 'ACTOR_FORM.BROKER.LABELS.RCP',
    placeholder: 'ACTOR_FORM.BROKER.PLACEHOLDERS.RCP',
  },
  [PlateformeFormFieldNames.Email]: {
    componentKey: ComponentKey.InputText,
    name: PlateformeFormFieldNames.Email,
    type: 'email',
    required: true,
    label: 'ACTOR_FORM.COMPAGNIE.LABELS.CORPORATE_EMAIL',
    placeholder: 'ACTOR_FORM.BROKER.PLACEHOLDERS.EMAIL',
    existenceCheck: {
      enabled: true,
      type: 'email',
      searchFilter: SearchFilterEnum.Email,
      debounceMs: 500,
      errorMessage: 'Cette adresse email existe déjà',
    },
  },
  ...themeInputsConfig
};
